import {
  type UploadFile,
  Button,
  Col,
  Flex,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd';
import { FormImagesUploadWrap } from '../formImagesField/styles';
import {
  isUploadFileListUploading,
  MultiImageUpload,
} from '../multiImageUpload';
import ReadOnlyFileUrlGallery from '../readOnlyFileUrlGallery';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUserId, selectUsername } from '../../slices/profile';
import {
  ActiveStatus,
  Cause,
  SuggestionStatus,
  UploadBusinessType,
  YesOrNo,
} from '../../types';
import {
  entityToUploadFileList,
  uploadFileListToEntityFiles,
} from '../../utils/entityUploadFiles';
import { createCause, updateCause } from '../../utils/httpRequest';

const CauseForm = ({
  form,
  templateID,
  onSubmit,
  onCancel,
  showFullScreenSpin,
  cause,
  isSuggestion = false,
  isSuggestionReview = false,
  onReview,
  onChange,
  readOnly,
}: {
  form?: FormInstance;
  templateID?: string;
  showFullScreenSpin?: boolean;
  onSubmit?: (cause: Cause) => Promise<void> | void;
  onReview?: (cause: Cause) => void;
  onChange?: (cause: Cause) => void;
  onCancel?: () => void;
  cause?: Cause;
  isSuggestion?: boolean;
  isSuggestionReview?: boolean;
  readOnly?: boolean;
}) => {
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const userId = useSelector(selectUserId);
  const username = useSelector(selectUsername);

  useEffect(() => {
    setUploadFileList(entityToUploadFileList(cause));
  }, [cause]);

  const handleCancel = () => {
    setUploadFileList([]);
    if (onCancel) {
      onCancel();
    }
  };

  const handleConfirm = async () => {
    if (isUploading) {
      return;
    }
    try {
      setLoading(true);
      await form?.validateFields();
      const filePayload = uploadFileListToEntityFiles(uploadFileList, cause);

      const newCause: Cause = {
        ...cause,
        ...form?.getFieldsValue(),
        ...filePayload,
        causeID: cause?.causeID || 0,
        templateID,
        activeStatus: ActiveStatus.ACTIVE,
        isSuggestion:
          cause?.isSuggestion || (isSuggestion ? YesOrNo.YES : YesOrNo.NO),
        suggestionStatus: cause?.suggestionStatus || SuggestionStatus.SUBMITTED,
        ...(isSuggestion &&
          !isSuggestionReview && {
            suggestionSubmitterID: userId,
            suggestionSubmitterName: username,
          }),
        ...(isSuggestionReview && {
          suggestionReviewerID: userId,
          suggestionReviewerName: username,
        }),
      };

      if (onReview) {
        onReview(newCause);
        return;
      }

      const res = await (cause ? updateCause(newCause) : createCause(newCause));

      if (res.data) {
        if (onSubmit) {
          onSubmit(res.data);
        }
        form?.resetFields();
        setUploadFileList([]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadChange = (list: UploadFile[]) => {
    setUploadFileList(list);
    setIsUploading(isUploadFileListUploading(list));
    const nextFiles = uploadFileListToEntityFiles(list, cause);
    onChange?.({ ...cause, ...nextFiles } as Cause);
  };

  return (
    <Spin spinning={!showFullScreenSpin && loading}>
      <Row gutter={[40, 16]}>
        <Col span={8}>
          {readOnly ? (
            <ReadOnlyFileUrlGallery images={cause?.images} />
          ) : (
            <FormImagesUploadWrap>
              <MultiImageUpload
                businessType={UploadBusinessType.CAUSE}
                value={uploadFileList}
                onChange={handleUploadChange}
                onUploadingChange={setIsUploading}
              />
            </FormImagesUploadWrap>
          )}
        </Col>
        <Col span={16}>
          <Flex vertical>
            <Form.Item
              label={
                <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                  Cause
                </span>
              }
              name="name"
              rules={[
                {
                  required: !readOnly,
                  message: 'Please input cause name!',
                },
              ]}
            >
              {readOnly ? (
                cause?.name
              ) : (
                <Input type="text" placeholder={t('enter')} />
              )}
            </Form.Item>
            <Form.Item
              label={
                <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                  Labels
                </span>
              }
              name="labels"
            >
              {readOnly ? (
                cause?.labels || '-'
              ) : (
                <Select
                  options={[{ value: 'label1', label: 'Label 1' }]}
                  placeholder={t('select')}
                />
              )}
            </Form.Item>
            <Form.Item
              label={
                <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                  Tools
                </span>
              }
              name="tools"
            >
              {readOnly ? (
                cause?.tools || '-'
              ) : (
                <Select
                  options={[{ value: 'tool1', label: 'Tools 1' }]}
                  placeholder={t('select')}
                />
              )}
            </Form.Item>
            <Form.Item
              label={
                <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                  {t('qualification')}
                </span>
              }
              name="qualification"
            >
              {readOnly ? (
                cause?.qualification || '-'
              ) : (
                <Select
                  options={[
                    { value: 'qualification1', label: 'Qualification 1' },
                  ]}
                  placeholder={t('select')}
                />
              )}
            </Form.Item>
            <Form.Item
              label={
                <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                  {t('description')}
                </span>
              }
              name="description"
              rules={[
                {
                  required: !readOnly,
                  message: 'Please input description!',
                },
              ]}
            >
              {readOnly ? (
                cause?.description || '-'
              ) : (
                <Input.TextArea placeholder={t('input')} rows={5} />
              )}
            </Form.Item>
            {!readOnly && !isSuggestionReview && (
              <Flex vertical gap={8} align="flex-end">
                {isUploading && (
                  <Typography.Text>
                    Image(s) upload in progress, please submit when it
                    completed!
                  </Typography.Text>
                )}
                <Flex justify="flex-end" gap={16}>
                  <Button type="primary" ghost onClick={handleCancel}>
                    {t('cancel')}
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleConfirm}
                    disabled={isUploading}
                  >
                    {t('confirm')}
                  </Button>
                </Flex>
              </Flex>
            )}
          </Flex>
          {showFullScreenSpin && <Spin spinning={loading} fullscreen />}
        </Col>
      </Row>
    </Spin>
  );
};

export default CauseForm;
